// frontend/src/pages/TransferDetails.tsx
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiTransferShow } from "../lib/api";

type Doc = { file_path?: string; path?: string };
type Recipient = { name: string | null };
type Transfer = {
  id: number;
  status: string;
  transfer_type: string;
  amount: number;
  currency: string;
  reference_number?: string | null;
  purpose_of_payment?: string | null;
  rejection_reason?: string | null;
  iban?: string | null;
  account_number?: string | null;
  bank_name?: string | null;
  bank_address?: string | null;
  bank_bic?: string | null;
  intermediary_bank_name?: string | null;
  intermediary_bank_bic?: string | null;
  created_at: string;
  executed_at?: string | null;
  rejected_at?: string | null;
  recipient?: Recipient | null;
  pofDocument?: Doc | null;
  sowDocument?: Doc | null;
};

export default function TransferDetails() {
  const { id } = useParams();
  const [t, setT] = useState<Transfer | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiTransferShow(Number(id));
        setT(res?.data ?? res); // support either {data} or entity
      } catch (e: any) {
        setError(e?.message ?? "Failed to load transfer.");
      }
    })();
  }, [id]);

  if (error) {
    return <div className="bg-red-100 text-red-800 p-4 rounded border border-red-300">{error}</div>;
  }
  if (!t) {
    return <div className="skeleton h-32 rounded" />;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 bg-white dark:bg-gray-800 p-6 rounded shadow text-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><strong>Transfer ID:</strong> {t.id}</div>
        <div><strong>Status:</strong> {String(t.status).charAt(0).toUpperCase() + String(t.status).slice(1)}</div>

        <div><strong>Recipient:</strong> {t.recipient?.name ?? "—"}</div>
        <div><strong>Type:</strong> {t.transfer_type?.toUpperCase()}</div>

        <div><strong>Amount:</strong> {Number(t.amount).toFixed(2)} {t.currency}</div>
        <div><strong>Reference:</strong> {t.reference_number ?? "—"}</div>

        <div className="md:col-span-2"><strong>Purpose:</strong> {t.purpose_of_payment ?? "—"}</div>

        {!!t.rejection_reason && (
          <div className="md:col-span-2 text-red-600">
            <strong>Rejection Reason:</strong> {t.rejection_reason}
          </div>
        )}

        <div><strong>IBAN:</strong> {t.iban ?? "—"}</div>
        <div><strong>Account Number:</strong> {t.account_number ?? "—"}</div>

        <div><strong>Bank Name:</strong> {t.bank_name ?? "—"}</div>
        <div><strong>Bank Address:</strong> {t.bank_address ?? "—"}</div>

        <div><strong>Bank BIC:</strong> {t.bank_bic ?? "—"}</div>
        <div>
          <strong>Intermediary Bank:</strong> {t.intermediary_bank_name ?? "—"} ({t.intermediary_bank_bic ?? "—"})
        </div>

        <div><strong>Created At:</strong> {t.created_at}</div>

        {t.status === "executed" && t.executed_at ? (
          <div><strong>Executed At:</strong> {t.executed_at}</div>
        ) : (["failed", "rejected"].includes(t.status) && t.rejected_at) ? (
          <div><strong>Rejected At:</strong> {t.rejected_at}</div>
        ) : null}
      </div>

      {/* Compliance Docs */}
      <div className="pt-6">
        <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Compliance Documents</h3>

        {t.pofDocument ? (
          <p>
            📄{" "}
            <a
              href={t.pofDocument.file_path ? `/storage/${t.pofDocument.file_path}` : "#"}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              Proof of Funds
            </a>
          </p>
        ) : (
          <p className="text-gray-500">No Proof of Funds document uploaded.</p>
        )}

        {t.sowDocument ? (
          <p>
            📄{" "}
            <a
              href={t.sowDocument.file_path ? `/storage/${t.sowDocument.file_path}` : "#"}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              Source of Wealth
            </a>
          </p>
        ) : (
          <p className="text-gray-500">No Source of Wealth document uploaded.</p>
        )}
      </div>

      <div className="pt-4">
        <Link to="/transfers" className="text-sm text-blue-600 hover:underline">
          &larr; Back to Transfer Instruction
        </Link>
      </div>
    </div>
  );
}
